import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Chart as ChartJS } from 'chart.js';
import { MatrixController, MatrixElement } from 'chartjs-chart-matrix';
import { Chart } from 'primereact/chart';
import { ProgressSpinner } from 'primereact/progressspinner';
import { UserService } from '../../../service/UserService';
import { useThemeVersion } from '../../../useTheme';

// Mesmo registro do MatrixController feito em ModulePerformanceHeatmap.js —
// idempotente, mas cada arquivo que usa type="matrix" precisa chamar.
ChartJS.register(MatrixController, MatrixElement);

const userService = new UserService();

// ~6 meses — período suficiente pra enxergar padrão de uso sem colunas
// finas demais num card de largura normal (sem scroll horizontal).
const WEEKS_BACK = 26;
const DAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTH_LABELS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

// Mesma paleta de 5 níveis (estilo GitHub) já usada em
// ModulePerformanceHeatmap.js — mantém os dois heatmaps do backoffice
// visualmente consistentes.
const GREEN_LEVELS = ['#ebedf0', '#9be9a8', '#40c463', '#30a14e', '#216e39'];
const hexToRgb = (hex) => {
    const match = (hex || '').trim().match(/^#?([0-9a-f]{6})$/i);
    if (!match) return [128, 128, 128];
    const n = parseInt(match[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

// Faixas em segundos (não percentual, diferente do heatmap de desempenho) —
// 0 / até 15min / até 1h / até 4h / acima de 4h.
const levelForDuration = (seconds) => {
    if (!seconds || seconds <= 0) return 0;
    if (seconds <= 15 * 60) return 1;
    if (seconds <= 60 * 60) return 2;
    if (seconds <= 4 * 60 * 60) return 3;
    return 4;
};
const colorForDuration = (seconds) => {
    const [r, g, b] = hexToRgb(GREEN_LEVELS[levelForDuration(seconds)]);
    return `rgb(${r}, ${g}, ${b})`;
};

const formatDateParam = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatDuration = (totalSeconds) => {
    const seconds = Math.round(totalSeconds || 0);
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${String(m).padStart(2, '0')}min`;
    if (m > 0) return `${m}min`;
    return seconds > 0 ? `${seconds}s` : 'sem sessão';
};

// Preenche TODOS os dias do intervalo (não só os com sessão, que é tudo que
// o backend retorna) — dia sem sessão vira célula "vazia" (nível 0), igual
// ao calendário de contribuições do GitHub, em vez de simplesmente não
// aparecer no grid.
const buildCells = (dailyData, start, end) => {
    const byDate = new Map((dailyData || []).map(row => [row.date, row]));
    const cells = [];
    const cursor = new Date(start);
    while (cursor <= end) {
        const dateKey = formatDateParam(cursor);
        const row = byDate.get(dateKey);
        // Semanas completas (domingo a sábado) a partir de `start`, que já
        // vem alinhado a um domingo — a data desse domingo é a chave da
        // coluna (ordena certo entre anos, sem depender de número de semana ISO).
        const weekStart = new Date(cursor);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());
        cells.push({
            date: dateKey,
            period: formatDateParam(weekStart),
            weekday: DAY_LABELS[cursor.getDay()],
            totalDurationSeconds: row?.totalDurationSeconds ?? 0,
            sessionCount: row?.sessionCount ?? 0,
        });
        cursor.setDate(cursor.getDate() + 1);
    }
    return cells;
};

// Rótulo de mês só na primeira coluna em que ele aparece (uma linha "jun ...
// jul ..." acima do grid) — mostrar o mês em toda coluna ficaria ilegível
// com ~26 semanas.
const buildMonthLabels = (periods) => {
    let lastMonth = null;
    return periods.map(period => {
        const month = new Date(`${period}T00:00:00`).getMonth();
        if (month === lastMonth) return '';
        lastMonth = month;
        return MONTH_LABELS[month];
    });
};

// Heatmap de calendário (estilo GitHub) do uso do app por um único usuário —
// fica no topo da página de Histórico de Atividades, acima da timeline
// cronológica, pra dar a visão "de longe" (frequência de uso ao longo dos
// meses) que a lista item-a-item não mostra.
// forwardRef + useImperativeHandle expõe `reload()` pro botão de refresh do
// header externo (na página) disparar os dois painéis num só Promise.all,
// sem esse componente precisar de reload próprio.
export const SessionsCalendarHeatmap = forwardRef(({ userId }, ref) => {
    const [data, setData] = useState([]);
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState(null);
    const themeVersion = useThemeVersion();

    const { start, end } = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const rawStart = new Date(today);
        rawStart.setDate(rawStart.getDate() - (WEEKS_BACK * 7 - 1));
        rawStart.setDate(rawStart.getDate() - rawStart.getDay());
        return { start: rawStart, end: today };
    }, []);

    const load = useCallback(() => {
        setStatus('loading');
        setError(null);
        const from = new Date(start).toISOString();
        const to = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999).toISOString();
        return userService.getDailyUsage(userId, { from, to })
            .then(res => {
                setData(Array.isArray(res) ? res : []);
                setStatus('success');
            })
            .catch(() => {
                setError('Não foi possível carregar o heatmap de sessões.');
                setStatus('error');
            });
    }, [userId, start, end]);

    useImperativeHandle(ref, () => ({ reload: load }), [load]);

    useEffect(() => {
        load();
    }, [load]);

    const cells = useMemo(() => buildCells(data, start, end), [data, start, end]);
    const periods = useMemo(() => Array.from(new Set(cells.map(c => c.period))).sort(), [cells]);
    const monthLabels = useMemo(() => buildMonthLabels(periods), [periods]);

    const { surfaceHex, textColor } = useMemo(() => {
        const style = getComputedStyle(document.documentElement);
        return {
            surfaceHex: style.getPropertyValue('--surface-card').trim() || '#fcfcfb',
            textColor: style.getPropertyValue('--text-color').trim() || '#495057',
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [themeVersion]);

    const chartData = useMemo(() => ({
        datasets: [{
            label: 'Sessões',
            data: cells.map(cell => ({
                x: cell.period,
                y: cell.weekday,
                date: cell.date,
                totalDurationSeconds: cell.totalDurationSeconds,
                sessionCount: cell.sessionCount,
            })),
            backgroundColor: (ctx) => colorForDuration(ctx.raw?.totalDurationSeconds),
            borderWidth: 1,
            borderColor: surfaceHex,
            width: ({ chart }) => Math.max(((chart.chartArea || {}).width || 0) / Math.max(periods.length, 1) - 2, 4),
            height: ({ chart }) => Math.max(((chart.chartArea || {}).height || 0) / 7 - 2, 4),
        }],
    }), [cells, periods, surfaceHex]);

    const chartOptions = useMemo(() => ({
        maintainAspectRatio: false,
        scales: {
            x: {
                type: 'category',
                labels: periods,
                offset: true,
                ticks: { color: textColor, callback: (_val, index) => monthLabels[index] },
                grid: { display: false },
            },
            y: {
                type: 'category',
                labels: DAY_LABELS,
                offset: true,
                ticks: { color: textColor },
                grid: { display: false },
            },
        },
        plugins: {
            legend: false,
            tooltip: {
                callbacks: {
                    title: (items) => {
                        const raw = items[0]?.raw;
                        if (!raw) return '';
                        return new Date(`${raw.date}T00:00:00`).toLocaleDateString('pt-BR', {
                            weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric',
                        });
                    },
                    label: (ctx) => {
                        const raw = ctx.raw;
                        if (!raw.sessionCount) return 'Sem sessão';
                        return `${formatDuration(raw.totalDurationSeconds)} — ${raw.sessionCount} sess${raw.sessionCount === 1 ? 'ão' : 'ões'}`;
                    },
                },
            },
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [periods, monthLabels, textColor]);

    // Sem card/título próprio nem botão de reload — vive dentro da aba
    // "Heatmap de Sessões" do accordion (que já dá o título) e a página já
    // tem um botão de reload no header externo. Mesmo padrão visual da aba
    // "Linha do Tempo" ao lado: spinner centralizado / mensagem de erro /
    // conteúdo, sem container extra.
    if (status === 'loading') {
        return (
            <div className="flex justify-content-center p-5">
                <ProgressSpinner />
            </div>
        );
    }
    if (error) {
        return <p>{error}</p>;
    }
    return <Chart type="matrix" data={chartData} options={chartOptions} height="220px" />;
});

export default SessionsCalendarHeatmap;
